import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Divider,
  FormHelperText,
  Grid,
  Switch,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';

import { addReturnOrder } from 'redux/actions';
import { VAT_UAE } from 'variables/globalVAT';

const CustomFooter = (props) => (
  <React.Fragment>
    {props.selectionModel.length > 0 ?
      <Grid item style={{padding: 8, display: 'flex'}}>
        <Button
          variant="contained"
          color="secondary"
          onClick={props.deleteTableRows}
        >
          Delete Product(s)
        </Button>
      </Grid>
    : null }
  </React.Fragment>
);

const ReturnOrderDetails = (props) => {

  const [productInput, setProductInput] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [vendorInput, setVendorInput] = useState({firstLetter: "", id: 0, name: "", phone: "", trn: ""});
  const [paymentByCash, setPaymentByCash] = useState(false);

  const [roProducts, setROProducts] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);

  const productsObj = useMemo(() => (
    props.products
      .map(product => {
        const firstLetter = product.name[0].toUpperCase();
        return {
          firstLetter,
          productId: product.id,
          displayId: product.displayId,
          name: product.name,
          category: product.category,
          company: product.company,
          unitCost: product.unitCost
        }
      })
      .filter(product => !roProducts.some(item => item.productId === product.productId))
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))

  ), [props.products, roProducts]);

  const vendorsObj = useMemo(() => (
    props.vendors
      .map(vendor => {
        const firstLetter = vendor.name[0].toUpperCase();
        return {firstLetter, id: vendor.id, name: vendor.name, phone: vendor.phone, trn: vendor.trn};
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
    
  ), [props.vendors]);

  const columns = [
    { field: 'displayId', headerName: 'Product ID', flex: 1 },
    { field: 'name', headerName: 'Product Name', flex: 1 },
    { field: 'category', headerName: 'Category', flex: 1 },
    { field: 'company', headerName: 'Brand', flex: 1 },
    { field: 'unitCost', headerName: 'Cost', type: 'number', flex: 1 },
    { field: 'quantity', headerName: 'Quantity', type: 'number', flex: 1, editable: true }
  ];

  const saveReturnOrder = () => {

    const subTotal = roProducts.reduce((prev, curr) => prev + (curr.unitCost * curr.quantity), 0);
    const totalVat = Math.round( subTotal * VAT_UAE );
    const grandTotal = subTotal + totalVat;
    
    const newReturnOrder = {
      displayId: props.createRODisplayId(),
      vendorId: vendorInput.id,
      totalVat,
      total: grandTotal,
      paymentByCash,
      products: roProducts.map(roProduct => ({
        productId: roProduct.productId,
        quantity: roProduct.quantity
      }))
    };
    props.addReturnOrder(newReturnOrder, props.activeUserId);
    
    const invoiceInfo = {
      displayId: newReturnOrder.displayId,
      vendorName: vendorInput.name,
      vendorTrn: vendorInput.trn,
      paymentDate: new Date(),
      subTotal,
      taxTotal: totalVat,
      grandTotal,
      paymentByCash,
      products: roProducts
    };
    props.openModal(invoiceInfo);
    returnOrderCleanup();
  };
  
  const returnOrderCleanup = () => {
    setVendorInput({firstLetter: "", id: 0, name: "", phone: "", trn: ""});
    setPaymentByCash(false);
    setROProducts([]);
    setSelectionModel([]);
    props.setOpen(false);
  };

  const checkProductBarcode = (e) => {
    setBarcodeInput(e.target.value);

    if(e.target.value.length > 3) {
      const productInfo = props.products.find(product => product.barcode === e.target.value);
      if(productInfo) {
        const index = roProducts.findIndex(roProduct => roProduct.productId === productInfo.id);
        //if the product is already added to returnOrder, increase its quantity.
        if( index > -1 ) {
          setROProducts([
            ...roProducts.slice(0, index),
            {
              ...roProducts[index],
              quantity: roProducts[index].quantity + 1
            },
            ...roProducts.slice(index + 1)
          ]);
        }
        //else add the product to the purchaseOrder
        else {
          //'id' field is added only for the DataGrid object.
          setROProducts([
            ...roProducts,
            {
              id: productInfo.displayId,
              displayId: productInfo.displayId,
              productId: productInfo.id,
              name: productInfo.name,
              category: productInfo.category,
              company: productInfo.company,
              unitCost: productInfo.unitCost,
              quantity: 1
            }
          ]);
        }
        setBarcodeInput("");
      }
    }
  };

  const handleEditCellChangeCommitted = React.useCallback(
    ({ id, field, props }) => {
      const quantityIndex = roProducts.findIndex(roProduct => roProduct.id === id);
      setROProducts([
        ...roProducts.slice(0, quantityIndex),
        {
          ...roProducts[quantityIndex],
          quantity: parseFloat(props.value) || roProducts[quantityIndex].quantity
        },
        ...roProducts.slice(quantityIndex + 1)
      ]);
    }, [roProducts, setROProducts]
  );

  const deleteTableRows = () => {
    setROProducts(roProducts.filter(roProduct => selectionModel.indexOf(roProduct.id) === -1));
    setSelectionModel([]);
  };

  return (
    <React.Fragment>
    <Card>
      <CardHeader
        subheader="Create Return Order"
        title="Return Order"
      />
      <Divider />
      <CardContent>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            md={6}
            xs={12}
          >
            <Autocomplete
              onChange={(event, newValue) => {
                //'id' field is added only for the DataGrid object.
                setROProducts([
                  ...roProducts,
                  {
                    id: newValue.displayId,
                    displayId: newValue.displayId,
                    productId: newValue.productId,
                    name: newValue.name,
                    category: newValue.category,
                    company: newValue.company,
                    unitCost: newValue.unitCost,
                    quantity: 1
                  }
                ]);
                setProductInput("");
              }}
              id="free-solo-dialog-demo"
              fullWidth
              options={productsObj}
              groupBy={(product) => product.firstLetter}
              getOptionSelected={(option, value) => option.productId === value.productId}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (<TextField {...params} label="Select Product.." variant="outlined" />)}
              inputValue={productInput}
              onInputChange={(event, newInputValue) => {
                setProductInput(newInputValue);
              }}
              value={null}
            />
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
          >
            <TextField
              fullWidth
              helperText="Scan Product Barcode"
              label="Scan Barcode"
              name="barcode"
              onChange={(event) => checkProductBarcode(event)}
              value={barcodeInput}
              variant="outlined"
            />
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
          >
            <Autocomplete
              onChange={(event, newValue) => {
                if(newValue) {
                  setVendorInput(newValue);
                }
                else {
                  setVendorInput({firstLetter: "", id: 0, name: "", phone: "", trn: ""});
                }
              }}
              id="free-solo-dialog-demo"
              fullWidth
              options={vendorsObj}
              groupBy={(vendor) => vendor.firstLetter}
              getOptionSelected={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.name} (${option.phone})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Vendor.."
                  variant="outlined"
                />
              )}
              value={vendorInput ? ( vendorInput.id ? vendorInput : null ) : null}
            />
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
            container
            direction="column"
            alignItems="center"
          >
            <FormHelperText>Select This if Cash will be received :</FormHelperText>
            <Switch
              checked={paymentByCash}
              onChange={() => setPaymentByCash(!paymentByCash)}
              color="primary"
            />
          </Grid>
        </Grid>
        <Box sx={{
          overflow: 'auto',
          height: 318,
          marginTop: '2%'
        }}>
          <DataGrid
            rows={roProducts}
            columns={columns}
            components={{
              Footer: CustomFooter
            }}
            componentsProps={{
              footer: { selectionModel, deleteTableRows }
            }}
            onEditCellChangeCommitted={handleEditCellChangeCommitted}
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel.selectionModel);
            }}
            selectionModel={selectionModel}
            checkboxSelection
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'No Products'
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <h3>Total : {roProducts.reduce((prev, curr) => prev + (curr.unitCost * curr.quantity) , 0)} AED</h3>
        </Box>
        <Button
          disabled={ !roProducts[0] || vendorInput.id === 0 }
          color="primary"
          variant="contained"
          onClick={saveReturnOrder}
        >
          Save R.O.
        </Button>
        <Button
          color="primary"
          onClick={returnOrderCleanup}
        >
          Cancel
        </Button>
      </CardContent>
      <Divider />
    </Card>
    </React.Fragment>
  )
};

export default connect (null, {addReturnOrder}) (ReturnOrderDetails);